import { useCallback, useEffect, useState } from 'react';
import type { RenderViewState, AvailableViewEntry, CustomView } from '../potree/viewer/RenderViewState';

interface UseRenderViewStateResult {
  availableViews: ReadonlyArray<AvailableViewEntry>;
  customViews: ReadonlyArray<CustomView>;
  currentViewId: string | null;
  setView: (viewId: string) => void;
}

/**
 * React hook over a RenderViewState. Returns the same shape as
 * `useViewManager` so existing components (`<ViewSelector>`) can be reused
 * by switching their data source via context, without changing their JSX.
 *
 * Subscribes to the two ReactiveProperties on the state container; React
 * re-renders when either fires.
 */
export function useRenderViewState(state: RenderViewState | null): UseRenderViewStateResult {
  const [availableViews, setAvailableViews] = useState<ReadonlyArray<AvailableViewEntry>>(() => state?.availableViews.value ?? []);
  const [customViews, setCustomViews] = useState<ReadonlyArray<CustomView>>(() => state?.customViews.value ?? []);
  const [currentViewId, setCurrentViewId] = useState<string | null>(() => state?.currentViewId.value ?? null);

  // Re-sync local state synchronously during render when `state` swaps
  // (e.g. widget unmount/remount). Pattern from React docs: detect the
  // change against a remembered previous value, call setState during
  // render, React discards the in-progress render. Avoids the
  // "setState in effect" lint rule that bans the equivalent useEffect.
  const [lastState, setLastState] = useState(state);
  if (lastState !== state) {
    setLastState(state);
    setAvailableViews(state?.availableViews.value ?? []);
    setCustomViews(state?.customViews.value ?? []);
    setCurrentViewId(state?.currentViewId.value ?? null);
  }

  useEffect(() => {
    if (!state) return;
    const subA = state.availableViews.subscribe((v) => setAvailableViews(v));
    const subB = state.customViews.subscribe((v) => setCustomViews(v));
    const subC = state.currentViewId.subscribe((v) => setCurrentViewId(v));
    return () => {
      subA.unsubscribe();
      subB.unsubscribe();
      subC.unsubscribe();
    };
  }, [state]);

  const setView = useCallback(
    (viewId: string) => {
      state?.setView(viewId);
    },
    [state],
  );

  return { availableViews, customViews, currentViewId, setView };
}
